export const TRIR_KEYS = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0];
export const TRIR_MODS = [0.9, 0.95, 1.0, 1.05, 1.1, 1.2, 1.3, 1.45];

// A 'General' row backed by fewer than MIN_SAMPLE states is not a national
// average and must not be presented as one.
const MIN_SAMPLE = 10;

export function fmt(n) {
	if (n === null || n === undefined || isNaN(n)) {
		return '—';
	}
	const sign = n < 0 ? '-' : '';
	return sign + '$' + Math.abs(Math.round(n)).toLocaleString('en-US');
}

export function getTrirMod(trir) {
	// Approximate match — largest TRIR_KEY <= trir
	let mod = TRIR_MODS[0];
	for (let i = 0; i < TRIR_KEYS.length; i++) {
		if (TRIR_KEYS[i] <= trir) {
			mod = TRIR_MODS[i];
		} else {
			break;
		}
	}
	return mod;
}

export function createRateResolver(lookup) {
	// How many real states actually carry a rate for each sector|industry pair.
	const coverage = {};
	for (const k in lookup) {
		const p = k.split('|');
		if (p[1] === 'General') {
			continue;
		}
		const id = p[0] + '|' + p[2];
		coverage[id] = (coverage[id] || 0) + 1;
	}

	const rate = (s, st, n) => {
		const v = lookup[s + '|' + st + '|' + n];
		if (v !== undefined) {
			return v > 0 ? v : null;
		}
		return null;
	};

	// Resolve a loss cost rate through a documented fallback chain.
	// State='General' is populated for every industry, so tier 2 always resolves.
	const resolveLc = (s, st, n) => {
		if (!s || !st || !n) {
			return null;
		}

		let v = rate(s, st, n);
		if (v !== null) {
			return {
				rate: v,
				exact: true,
				basis:
					st === 'General'
						? 'National average rate'
						: st + ' state rate',
				note: '',
			};
		}

		const sample = coverage[s + '|' + n] || 0;
		if (sample >= MIN_SAMPLE) {
			v = rate(s, 'General', n);
			if (v !== null) {
				return {
					rate: v,
					exact: false,
					basis:
						'National average for this industry (' +
						sample +
						' states)',
					note:
						'No ' +
						st +
						'-specific rate is on file for this industry. Using the national average.',
				};
			}
		}

		v = rate(s, st, 'General');
		if (v !== null) {
			return {
				rate: v,
				exact: false,
				basis: st + ' ' + s + ' sector average',
				note:
					sample > 0 && sample < MIN_SAMPLE
						? 'This broad category is rated in only ' +
							sample +
							' state(s), so it has no defensible national average. Using the ' +
							st +
							' ' +
							s +
							' sector average instead. For a tighter number, pick a more specific industry.'
						: 'This industry is not rated in ' +
							st +
							'. Using the ' +
							st +
							' sector average.',
			};
		}

		v = rate(s, 'General', 'General');
		if (v !== null) {
			return {
				rate: v,
				exact: false,
				basis: s + ' national sector average',
				note: 'No state or industry rate is on file. Using the sector national average.',
			};
		}

		return null;
	};

	return { rate, resolveLc };
}
